import { DeviceConfig, Gateway, ResponseDto } from "types";
import {
    createErrorResponseStandard,
    createResponseStandard,
    orgIdQuery,
} from "utils/functions";
import { gatewayClient } from "service/gatewayServiceClients";
import { gatewayServiceQueryKeys } from "./gatewayQueryKeys";
import { serviceQueryClient } from "./queryClient";

const registryCachePolicy = {
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 0, // 0 minutes
    retry: 1 as number | boolean,
};

type RegistryCacheOptions = Partial<typeof registryCachePolicy>;

const fetchRegistryInfo = async (
    gatewayId: string
): Promise<ResponseDto<Gateway>> => {
    try {
        const response = await gatewayClient.getGwById(gatewayId);
        return createResponseStandard<Gateway>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

const fetchRegistryConfig = async (
    gatewayId: string
): Promise<ResponseDto<DeviceConfig[]>> => {
    try {
        const response = await gatewayClient.getGwConfigById(gatewayId);
        return createResponseStandard<DeviceConfig[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

const getGWReading = async (org: {
    organization_id: string;
}) => {
    try {
        const response = await gatewayClient.getGwReadingByOrganizationId(
            org
        );
        return createResponseStandard<object[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

const getGwAndLDSUStatus = async (
    gatewayId: string,
    query: {
        time_range?: string;
    } = {}
) => {
    try {
        const response = await gatewayClient.getDevicesReadingByGwId(
            gatewayId,
            query
        );
        return createResponseStandard<object[]>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

export const getRegistryInfoCached = async (
    gatewayId: string,
    options: RegistryCacheOptions = {}
): Promise<ResponseDto<Gateway>> =>
    serviceQueryClient.fetchQuery({
        queryKey: gatewayServiceQueryKeys.registryInfo(gatewayId),
        queryFn: () => fetchRegistryInfo(gatewayId),
        staleTime: options.staleTime ?? registryCachePolicy.staleTime,
        cacheTime: options.cacheTime ?? registryCachePolicy.cacheTime,
    });

export const getRegistryConfigCached = async (
    gatewayId: string,
    options: RegistryCacheOptions = {}
): Promise<ResponseDto<DeviceConfig[]>> => {
    return serviceQueryClient.fetchQuery({
        queryKey: gatewayServiceQueryKeys.registryConfig(gatewayId),
        queryFn: () => fetchRegistryConfig(gatewayId),
        staleTime: options.staleTime ?? registryCachePolicy.staleTime,
        cacheTime: options.cacheTime ?? registryCachePolicy.cacheTime,
    });
};

export const getGWReadingCached = async (
    options: RegistryCacheOptions = {}
): Promise<ResponseDto<object[]>> => {
    const org = orgIdQuery();
    return serviceQueryClient.fetchQuery({
        queryKey: gatewayServiceQueryKeys.gwReading(org),
        queryFn: () => getGWReading(org),
        staleTime: options.staleTime ?? registryCachePolicy.staleTime,
        cacheTime: options.cacheTime ?? registryCachePolicy.cacheTime,
    });
}

export const getGwAndLDSUStatusCached = async (
    gatewayId: string,
    query: {
        time_range?: string;
    } = {},
    options: RegistryCacheOptions = {}
) =>
    serviceQueryClient.fetchQuery({
        queryKey: gatewayServiceQueryKeys.gwAndLDSUStatus(gatewayId, query),
        queryFn: () => getGwAndLDSUStatus(gatewayId, query),
        staleTime: options.staleTime ?? registryCachePolicy.staleTime,
        cacheTime: options.cacheTime ?? registryCachePolicy.cacheTime,
    });

export const invalidateRegistryInfo = (gatewayId?: string) =>
    serviceQueryClient.invalidateQueries({
        queryKey: gatewayId
            ? gatewayServiceQueryKeys.registryInfo(gatewayId)
            : gatewayServiceQueryKeys.all,
        refetchType: "none",
    });

export const clearRegistryInfo = (gatewayId?: string) =>
    serviceQueryClient.removeQueries({
        queryKey: gatewayId
            ? gatewayServiceQueryKeys.registryInfo(gatewayId)
            : gatewayServiceQueryKeys.all,
    });